import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of, tap, throwError } from 'rxjs';
import type { Image, ImageResponse } from '../models/image-model';
import { environment } from '../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { BlogPost } from '../models/blog-post.models';
import { siteConfig } from '../config/site-config';

@Injectable({
  providedIn: 'root'
})
export class ImageService {
  private apiUrl = `${environment.apiUrl}/images`;
  private isBrowser: boolean;
  private imageCache = new Map<string, string>();
  private readonly liveHost = (() => {
    try {
      return new URL(siteConfig.urls.baseUrl).hostname.toLowerCase().replace(/^www\./, '');
    } catch {
      return '';
    }
  })();

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  private isOwnAbsoluteUrl(rawUrl: string): boolean {
    if (!this.liveHost) return false;
    try {
      const host = new URL(rawUrl).hostname.toLowerCase().replace(/^www\./, '');
      return host === this.liveHost;
    } catch {
      return false;
    }
  }

  generateAltTextAi(imageBase64: string): Observable <string> {
    return this.http.post<{message: string, altText: string}>(this.apiUrl + '/genAltTextAi', { base64: imageBase64})
      .pipe(
        map(res => res.altText || ''),
        catchError((error: HttpErrorResponse) => {
          console.log('Fehler beim Speichern des Blog-Posts', error);
          return throwError(() => error);
        })
      )
  }

  // Lädt Bild über HttpClient als Blob (nicht für LCP verwenden)
  getImageAsBlob(imageUrl: string): Observable<string> {
    if (this.imageCache.has(imageUrl)) {
      return of(this.imageCache.get(imageUrl)!);
    }

    let finalUrl = imageUrl;
    if (!environment.production && this.isOwnAbsoluteUrl(imageUrl)) {
      try {
        const url = new URL(imageUrl);
        finalUrl = url.pathname + url.search;
      } catch {
        finalUrl = imageUrl;
      }
    }

    return this.http.get(finalUrl, { responseType: 'blob' }).pipe(
      map(blob => {
        const blobUrl = URL.createObjectURL(blob);
        this.imageCache.set(imageUrl, blobUrl);
        return blobUrl;
      }),
      catchError(error => {
        console.error('Fehler beim Laden des Bildes:', error);
        return of('/assets/images/fallback.webp');
      })
    );
  }

  // Zentrale URL-Funktion: kein DPR-Multiplikator mehr, Negotiation via format=auto
  getOptimizedImageUrl(
    imageNameOrLink: string,
    options: { width?: number; quality?: number; format?: 'webp'|'jpeg'|'png'|'avif'|'auto' } = {}
  ): string {
    if (!imageNameOrLink) return '/assets/images/fallback.webp';

    // Dateinamen extrahieren (nur letzter Segmentteil)
    let fileName = imageNameOrLink;
    if (imageNameOrLink.includes('/')) {
      const parts = imageNameOrLink.split('/');
      fileName = parts[parts.length - 1] || imageNameOrLink;
    }

    // Basisroute: Haupt-/optimized-Handler nimmt Query-Params entgegen
    const base = environment.production
      ? `${environment.apiUrl}/images/${fileName}`
      : `/api/images/${fileName}`;

    const params = new URLSearchParams();
    if (options.width) params.append('width', String(Math.round(options.width)));
    if (options.quality) params.append('quality', String(options.quality));
    params.append('format', options.format || 'auto'); // Negotiation: AVIF bevorzugt

    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }

  // Srcset-Erzeugung mit deinen Backend-Buckets
  // Empfohlene Buckets je nach Komponente (z. B. 384/768/1152 für ~400px Renderbreite)
  buildSrcset(
    imageNameOrLink: string,
    widths: number[],
    opts: { quality?: number; format?: 'webp'|'jpeg'|'png'|'avif'|'auto' } = { format: 'auto' }
  ): string {
    return widths
      .map(w => `${this.getOptimizedImageUrl(imageNameOrLink, {
        width: w,
        quality: opts.quality ?? 80,
        format: opts.format ?? 'auto'
      })} ${w}w`)
      .join(', ');
  }

  // Für LCP-Bilder (Hero, größtes Bild im Viewport)
  getLcpImageUrl(
    imageNameOrLink: string,
    options: { sizes?: string; quality?: number } = {}
  ): {
    srcset: string;
    sizes: string;
    src: string;
    loading: 'eager' | 'lazy';
    fetchpriority: 'high' | 'auto';
  } {
    const widths = [384, 768, 1152]; // exakt zu deinen Buckets passend
    const quality = options.quality ?? 80;

    const srcset = this.buildSrcset(imageNameOrLink, widths, { quality, format: 'auto' });
    const src = this.getOptimizedImageUrl(imageNameOrLink, { width: 768, quality, format: 'auto' });

    return {
      srcset,
      sizes: options.sizes || '(max-width: 600px) 100vw, 60vw',
      src,
      loading: 'eager',
      fetchpriority: 'high'
    };
  }

  // Lädt Bild mit Fallback aus BlogPost-Daten
  getImageData(post: BlogPost, imageId: string) {
    if (!post || !post.imagesMetaData || !post.imagesMetaData.length) {
      return {
        height: 800,
        width: 600,
        link: '/assets/images/fallback.webp',
        alt: 'Ein Bild über das Thema Kräuter.'
      };
    }
    const image = post.imagesMetaData.find(img => img._id === imageId);
    if (!image) {
      return {
        height: 800,
        width: 600,
        link: '/assets/images/fallback.webp',
        alt: 'Ein Bild über das Thema Kräuter.'
      };
    }
    return image;
  }

  // Basis-URL (z. B. für Preloads) – kein festes webp mehr
  getBaseOptimizedUrl(imageUrl: string): string {
    if (!imageUrl) return '';
    let fileName = imageUrl;
    if (imageUrl.includes('/')) {
      fileName = imageUrl.split('/').pop() || '';
    }
    if (environment.production) {
      return `${environment.apiUrl}/images/${fileName}?format=auto`;
    } else {
      return `/api/images/${fileName}?format=auto`;
    }
  }

  // Interne Link-Transformation – .webp nur für reine ObjectId-Fälle ergänzen
  transformImageLink(link: string): string {
    if (!link) return '/assets/images/fallback.webp';

    // Externe URLs unangetastet lassen (keine Endung anhängen!)
    if (link.startsWith('http')) {
      if (!environment.production) {
        try {
          const url = new URL(link);
          if (this.isOwnAbsoluteUrl(link)) {
            return url.pathname + url.search;
          }
        } catch {
          // Ignorieren, wenn Parsing fehlschlägt
        }
      }
      return link;
    }

    // Dev: relative Pfade durch Proxy leiten
    const ensureExtension = (p: string) => {
      const hasExt = this.hasImageExtension(p);
      // Wenn es wie eine Mongo ObjectId aussieht, Endung ergänzen
      if (!hasExt && /^[a-fA-F0-9]{24}$/.test(p)) {
        return `${p}.webp`;
      }
      return p;
    };

    if (!environment.production) {
      if (link.startsWith('/api/')) return ensureExtension(link);
      if (link.startsWith('/images/')) return `/api${ensureExtension(link)}`;
      if (link.startsWith('images/')) return `/api/${ensureExtension(link)}`;
      // sonst: vermutlich ID oder relativer Pfad
      return `/api/images/${ensureExtension(link)}`;
    }

    // Production: absolute URLs anhand environment.apiUrl aufbauen
    const baseUrl = environment.apiUrl;
    const withExt = ensureExtension(link);

    if (withExt.startsWith('/images/')) return `${baseUrl}${withExt}`;
    if (withExt.startsWith('images/')) return `${baseUrl}/${withExt}`;
    if (!withExt.includes('/')) return `${baseUrl}/images/${withExt}`;

    const path = withExt.startsWith('/') ? withExt : `/${withExt}`;
    return `${baseUrl}${path}`;
  }

  private hasImageExtension(url: string): boolean {
    const extensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.avif'];
    return extensions.some(ext => url.toLowerCase().endsWith(ext));
  }

  // Hilfen für Blob-URLs (optional)
  cleanupBlobUrl(url: string): void {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
      for (const [key, value] of this.imageCache.entries()) {
        if (value === url) {
          this.imageCache.delete(key);
          break;
        }
      }
    }
  }

  clearImageCache(): void {
    for (const blobUrl of this.imageCache.values()) {
      if (blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
    }
    this.imageCache.clear();
  }

  // API: Liste Bilder
  getImages(
    page: number = 1,
    limit: number = 20,
    filters: { category?: string, keywords?: string, search?: string, sort?: string } = {}
  ): Observable<ImageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    params = params.set('sort', filters.sort || '-createdAt,_id');

    if (filters.category) params = params.set('category', filters.category);
    if (filters.keywords) params = params.set('keywords', filters.keywords);
    if (filters.search) params = params.set('search', filters.search);

    const url = environment.production ? this.apiUrl : '/api/images';

    return this.http.get<ImageResponse>(url, { params }).pipe(
      map(response => {
        if (response.images && Array.isArray(response.images)) {
          response.images = response.images.map(image => {
            image.link = this.transformImageLink(image.link);
            return image;
          });
        }
        return response;
      })
    );
  }

  // API: Einzelnes Bild
  getImage(id: string): Observable<Image> {
    const url = environment.production ? `${this.apiUrl}/${id}` : `/api/images/${id}`;
    return this.http.get<Image>(url).pipe(
      map(image => {
        image.link = this.transformImageLink(image.link);
        return image;
      })
    );
  }

  // API: Upload
  uploadImage(formData: FormData): Observable<HttpEvent<{ message: string, image: Image }>> {
    const url = environment.production ? `${this.apiUrl}/upload` : '/api/images/upload';
    return this.http.post<{ message: string, image: Image }>(
      url,
      formData,
      {
        reportProgress: true,
        observe: 'events'
      }
    );
  }

  // API: Update
  updateImage(id: string, data: { category: string, alt: string, keywords: string[] | string }): Observable<{ message: string, image: Image }> {
    const url = environment.production ? `${this.apiUrl}/${id}` : `/api/images/${id}`;
    return this.http.put<{ message: string, image: Image }>(
      url,
      data,
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response' as const
      }
    ).pipe(
      tap(response => console.log('Full response:', response)),
      map(response => {
        const responseBody = response.body as { message: string, image: Image };
        if (responseBody?.image?.link) {
          responseBody.image.link = this.transformImageLink(responseBody.image.link);
        }
        return responseBody;
      }),
      catchError(error => {
        console.error('Error in PUT request:', error);
        return throwError(() => error);
      })
    );
  }

  // API: Delete
  deleteImage(id: string): Observable<{ message: string }> {
    const url = environment.production ? `${this.apiUrl}/${id}` : `/api/images/${id}`;
    return this.http.delete<{ message: string }>(url);
  }

  // API: Relevante Bilder
  findRelevantImages(
    keywords: string[] | string,
    category?: string,
    mainTopic?: string
  ): Observable<ImageResponse> {
    const url = environment.production ? `${this.apiUrl}/relevant` : '/api/images/relevant';
    return this.http.post<ImageResponse>(url, {
      keywords,
      category,
      mainTopic
    }).pipe(
      map(response => {
        if (response.images && Array.isArray(response.images)) {
          response.images = response.images.map(image => {
            image.link = this.transformImageLink(image.link);
            return image;
          });
        }
        return response;
      })
    );
  }
}


